import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Braces, Calendar, ChevronRight, CircleSlash, Copy, CornerDownLeft, Hash, Image as ImageIcon, Link2, List, Search, ToggleLeft, Type, X, type LucideIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { QueryError } from '@/components/query-state'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { DataSource, ExploreNode } from '@/types/api'
import { exploreOptions, type ExploreParams } from '../api'
import { absorbNode, exploreValue, filterTree, forgetBelow, isBindableNode, isContainer, nodeId, nodeTitle, sampleText, typeLabel, type ExploreTree } from '../lib/explore'
import { copyBindingPath } from './copy-binding'

const PAGE = 50

export interface DataExplorerProps {
  /** Source to explore through the API (`/explore`). */
  source?: Pick<DataSource, 'id' | 'key' | 'type'> | null
  /** …or a local value (unsaved test result), explored the same way. */
  local?: { alias: string; value: unknown } | null
  /** Row click selects a value (binding picker). Without it, clicking copies the binding. */
  onSelect?: (path: string) => void
  /** "Use in binding" per value (binding picker inserts it). Without it: "Copy binding". */
  onUse?: (path: string) => void
  selectedPath?: string | null
  className?: string
  /** Tree height cap. */
  maxHeightClass?: string
}

type Loader = (params: ExploreParams) => Promise<ExploreNode>

/** Params of the explorer's root request (shared cache: pages can read `snapshot` / `computed`). */
export function explorerRootParams(key: string): ExploreParams {
  return { path: key, depth: 1, limit: PAGE }
}

/**
 * Visual data tree: type icons, sample values, keyed lists ("Green tea (id 123)") with an
 * "Identify items by" selector, lazy expansion, "Load more" paging and search over what is
 * loaded. Scalars offer "Copy binding" / "Use in binding" with `{{ path }}`.
 */
export function DataExplorer({ source, local, onSelect, onUse, selectedPath, className, maxHeightClass = 'max-h-[32rem]' }: DataExplorerProps) {
  const qc = useQueryClient()
  const alias = local?.alias ?? source?.key ?? ''
  const rootParams = useMemo(() => explorerRootParams(alias), [alias])
  const remote = useQuery({ ...exploreOptions(source?.id ?? '', rootParams), enabled: !local && !!source })

  const localRoot = useMemo(() => {
    if (!local) return null
    const r = exploreValue(local.value, local.alias, local.alias, { depth: 1, limit: PAGE })
    return 'node' in r ? r.node : null
  }, [local])
  const root = local ? localRoot : (remote.data?.node ?? null)

  const load: Loader = useCallback(
    async (params) => {
      if (local) {
        const r = exploreValue(local.value, local.alias, params.path, { keyField: params.key_field, offset: params.offset, limit: params.limit, depth: params.depth })
        if ('error' in r) throw new Error(r.error)
        return r.node
      }
      const res = await qc.fetchQuery(exploreOptions(source!.id, params))
      if (!res.node) throw new Error('No data yet')
      return res.node
    },
    [local, qc, source],
  )

  if (!local && remote.isPending) return <Skeleton className="h-48" />
  if (!local && remote.isError) return <QueryError error={remote.error} onRetry={() => void remote.refetch()} />
  if (!root) return <p className="p-4 text-center text-[13px] text-muted-foreground">No data to explore yet.</p>

  const version = remote.data?.snapshot ? `${remote.data.snapshot.version}@${remote.data.snapshot.fetched_at}` : 'none'
  return <ExplorerTree key={`${alias}:${version}`} root={root} load={load} onSelect={onSelect} onUse={onUse} selectedPath={selectedPath} className={className} maxHeightClass={maxHeightClass} />
}

interface TreeCtx {
  tree: ExploreTree
  meta: Record<string, ExploreNode>
  expanded: ReadonlySet<string>
  visible: ReadonlySet<string> | null
  loading: ReadonlySet<string>
  errors: Readonly<Record<string, string>>
  toggle: (node: ExploreNode, id: string) => void
  more: (node: ExploreNode, id: string) => void
  setKeyField: (node: ExploreNode, id: string, field: string) => void
  onSelect?: (path: string) => void
  onUse?: (path: string) => void
  selectedPath?: string | null
}

function ExplorerTree({ root, load, onSelect, onUse, selectedPath, className, maxHeightClass }: { root: ExploreNode; load: Loader; onSelect?: (p: string) => void; onUse?: (p: string) => void; selectedPath?: string | null; className?: string; maxHeightClass: string }) {
  const rootId = root.path ?? root.name
  const [tree, setTree] = useState<ExploreTree>(() => absorbNode({}, root))
  /** Latest node object per id (after a key-field change). */
  const [meta, setMeta] = useState<Record<string, ExploreNode>>({})
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([rootId]))
  const [keyFields, setKeyFields] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<Set<string>>(new Set())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [query, setQuery] = useState('')

  // A refetched root (same snapshot) refreshes its page without collapsing the tree.
  useEffect(() => {
    setTree((t) => absorbNode(t, root))
  }, [root])

  const filter = useMemo(() => filterTree(root, tree, query), [root, tree, query])
  const shownExpanded = useMemo(() => (filter ? new Set([...expanded, ...filter.expand]) : expanded), [expanded, filter])

  const run = async (id: string, params: ExploreParams, apply: (t: ExploreTree, node: ExploreNode) => ExploreTree) => {
    setLoading((s) => new Set(s).add(id))
    setErrors(({ [id]: _drop, ...rest }) => rest)
    try {
      const node = await load(params)
      setTree((t) => apply(t, node))
      setMeta((m) => ({ ...m, [id]: { ...node, children: undefined } }))
    } catch (e) {
      setErrors((x) => ({ ...x, [id]: e instanceof ApiError ? (e.errors.path?.[0] ?? e.detail) : e instanceof Error ? e.message : 'Couldn’t load this part' }))
    } finally {
      setLoading((s) => {
        const n = new Set(s)
        n.delete(id)
        return n
      })
    }
  }

  const ctx: TreeCtx = {
    tree,
    meta,
    expanded: shownExpanded,
    visible: filter?.visible ?? null,
    loading,
    errors,
    onSelect,
    onUse,
    selectedPath,
    toggle: (node, id) => {
      const open = expanded.has(id)
      setExpanded((s) => {
        const n = new Set(s)
        if (open) n.delete(id)
        else n.add(id)
        return n
      })
      if (!open && !tree[id] && node.path) void run(id, { path: node.path, depth: 1, limit: PAGE, key_field: keyFields[id] }, (t, n) => absorbNode(t, n, false, id))
    },
    more: (node, id) => {
      const page = tree[id]
      if (!node.path || !page) return
      void run(id, { path: node.path, depth: 1, limit: PAGE, offset: page.nextOffset, key_field: keyFields[id] }, (t, n) => absorbNode(t, n, true, id))
    },
    setKeyField: (node, id, field) => {
      if (!node.path) return
      setKeyFields((k) => ({ ...k, [id]: field }))
      void run(id, { path: node.path, depth: 1, limit: PAGE, key_field: field }, (t, n) => absorbNode(forgetBelow(t, id), n, false, id))
    },
  }

  const rootPage = tree[rootId]
  return (
    <div className={cn('grid min-w-0 gap-2', className)}>
      {isContainer(root) && (
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search loaded fields and values…" aria-label="Search the data" className="h-8 pl-8 text-[13px]" />
          {query && (
            <button type="button" className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" onClick={() => setQuery('')} aria-label="Clear search">
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}
      {filter && filter.matches === 0 && <p className="px-1 text-[12px] text-muted-foreground">Nothing loaded matches “{query.trim()}”. Expand lists to search inside them.</p>}
      <div className={cn('overflow-auto rounded-lg border bg-muted/20 p-1.5 scrollbar-thin', maxHeightClass)}>
        {isContainer(root) ? (
          <ul className="text-[13px]" aria-label="Data">
            {root.type === 'array' && <KeySelectorRow node={ctx.meta[rootId] ?? root} id={rootId} depth={0} ctx={ctx} />}
            <Children parent={root} parentId={rootId} page={rootPage} depth={0} ctx={ctx} isRoot />
          </ul>
        ) : (
          <ul className="text-[13px]">
            <Row node={root} id={rootId} parent={null} depth={0} ctx={ctx} />
          </ul>
        )}
      </div>
    </div>
  )
}

function Children({ parent, parentId, page, depth, ctx, isRoot = false }: { parent: ExploreNode; parentId: string; page: ExploreTree[string] | undefined; depth: number; ctx: TreeCtx; isRoot?: boolean }) {
  if (!page) return null
  const parentMeta = ctx.meta[parentId] ?? parent
  return (
    <>
      {page.children.map((c) => {
        const id = nodeId(c, parentId)
        if (ctx.visible && !ctx.visible.has(id)) return null
        return <Row key={id} node={c} id={id} parent={parentMeta} parentIsRoot={isRoot} depth={depth} ctx={ctx} />
      })}
      {page.truncated && !ctx.visible && (
        <li style={{ paddingLeft: depth * 16 + 26 }} className="py-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[12px]" loading={ctx.loading.has(parentId)} onClick={() => ctx.more(parent, parentId)}>
            Load more <span className="text-muted-foreground">({page.children.length} of {page.count} shown)</span>
          </Button>
        </li>
      )}
    </>
  )
}

const ICONS: Record<ExploreNode['type'], LucideIcon> = { object: Braces, array: List, string: Type, integer: Hash, number: Hash, boolean: ToggleLeft, null: CircleSlash }

function iconFor(node: ExploreNode): LucideIcon {
  if (node.type === 'string' && node.format) return node.format === 'image' ? ImageIcon : node.format === 'url' ? Link2 : node.format === 'numeric' ? Hash : Calendar
  return ICONS[node.type]
}

function Row({ node, id, parent, parentIsRoot = false, depth, ctx }: { node: ExploreNode; id: string; parent: ExploreNode | null; parentIsRoot?: boolean; depth: number; ctx: TreeCtx }) {
  const current = ctx.meta[id] ?? node
  const container = isContainer(current)
  const canOpen = container && (!!current.path || !!ctx.tree[id]) && (current.count ?? 0) > 0
  const open = canOpen && ctx.expanded.has(id)
  const bindable = isBindableNode(current)
  const selected = !!current.path && current.path === ctx.selectedPath
  const title = nodeTitle(current, parent, parentIsRoot)
  const Icon = iconFor(current)
  const sample = sampleText(current)

  const activate = () => {
    if (canOpen) ctx.toggle(current, id)
    else if (bindable && current.path) {
      if (ctx.onSelect) ctx.onSelect(current.path)
      else void copyBindingPath(current.path)
    }
  }

  return (
    <li>
      <div className={cn('group flex min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-muted/70', selected && 'bg-primary/10 ring-1 ring-primary/30')} style={{ paddingLeft: depth * 16 + 4 }}>
        {canOpen ? (
          <button type="button" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`} className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground" onClick={() => ctx.toggle(current, id)}>
            <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className="size-5 shrink-0" />
        )}
        <button
          type="button"
          onClick={activate}
          disabled={!canOpen && !bindable}
          title={current.unbindable ? 'This field name has spaces or symbols, so it can’t be used in a binding.' : (current.path ?? undefined)}
          className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-left outline-none focus-visible:underline disabled:cursor-default"
        >
          <Icon className={cn('size-3.5 shrink-0', container ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
          <span className={cn('min-w-0 shrink truncate font-medium', current.unbindable && 'text-muted-foreground')}>{title}</span>
          <span className="shrink-0 text-[11.5px] text-muted-foreground">{typeLabel(current)}</span>
          {sample && <span className={cn('min-w-0 truncate text-[12.5px]', current.type === 'null' ? 'text-muted-foreground italic' : 'text-foreground/80')}>{sample}</span>}
        </button>
        {ctx.loading.has(id) && !open && <span className="text-[11px] text-muted-foreground">Loading…</span>}
        {bindable && current.path && (
          <button
            type="button"
            onClick={() => (ctx.onUse ? ctx.onUse(current.path!) : void copyBindingPath(current.path!))}
            className={cn('flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11.5px] font-medium text-primary opacity-0 group-hover:opacity-100 hover:bg-primary/10 focus-visible:opacity-100', selected && 'opacity-100')}
            aria-label={ctx.onUse ? `Use ${title} in binding` : `Copy binding for ${title}`}
          >
            {ctx.onUse ? <CornerDownLeft className="size-3" /> : <Copy className="size-3" />} <span className="hidden sm:inline">{ctx.onUse ? 'Use in binding' : 'Copy binding'}</span>
          </button>
        )}
      </div>
      {ctx.errors[id] && (
        <p role="alert" className="text-[12px] text-destructive" style={{ paddingLeft: depth * 16 + 30 }}>
          {ctx.errors[id]}
        </p>
      )}
      {open && (
        <ul>
          {current.type === 'array' && <KeySelectorRow node={current} id={id} depth={depth + 1} ctx={ctx} />}
          {!ctx.tree[id] && ctx.loading.has(id) ? (
            <li style={{ paddingLeft: (depth + 1) * 16 + 30 }} className="py-1">
              <Skeleton className="h-4 w-40" />
            </li>
          ) : (
            <Children parent={current} parentId={id} page={ctx.tree[id]} depth={depth + 1} ctx={ctx} />
          )}
        </ul>
      )}
    </li>
  )
}

/** "Identify items by: [id ▾]" for lists of objects. */
function KeySelectorRow({ node, id, depth, ctx }: { node: ExploreNode; id: string; depth: number; ctx: TreeCtx }) {
  const candidates = node.key_candidates ?? []
  if (node.item_type !== 'object' || (!candidates.length && !node.key_error)) return null
  const value = node.key_field ?? 'none'
  return (
    <li className="flex flex-wrap items-center gap-2 py-1 text-[12px] text-muted-foreground" style={{ paddingLeft: depth * 16 + 30 }}>
      <span id={`${id}-keyed`}>Identify items by</span>
      <Select value={value} onValueChange={(v) => ctx.setKeyField(node, id, v)} disabled={ctx.loading.has(id) || !node.path}>
        <SelectTrigger className="h-7 w-auto min-w-28 text-[12px]" aria-labelledby={`${id}-keyed`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
          <SelectItem value="none">Position</SelectItem>
        </SelectContent>
      </Select>
      <span className="text-[11.5px]">{node.key_field ? 'Bindings follow the item even if the list is reordered.' : 'Bindings point at a position in the list.'}</span>
      {node.key_error && <span className="w-full text-destructive">{node.key_error}</span>}
    </li>
  )
}
